// Cardholder terminology (patrons / customers / members / users) — a global
// per-partner setting chosen when Communico onboards a partner (backend:
// client_settings row "cardholder_terminology"; the owning surface is
// backend/internal/service/library_terminology.go). Consumers read it
// off their module context bundle (e.g. GET /attend/context ->
// settings.cardholder_terminology) or GET /library/terminology. A closed vocabulary: this pure helper is the ONE place that
// maps a stored value to display copy, so a fourth term is a one-line
// code change here, not a hunt through every consumer.
//
// Defaults to "customers" on an absent or out-of-vocabulary value — the
// same fallback the backend applies (resolveCardholderTerminology in
// library_terminology.go) — so a stale/hand-edited setting can never
// surface a shape this helper doesn't know how to render.

export interface CardholderTerm {
	singular: string;
	plural: string;
	Singular: string;
	Plural: string;
}

const TERMS: Record<string, { singular: string; plural: string }> = {
	patrons: { singular: "patron", plural: "patrons" },
	customers: { singular: "customer", plural: "customers" },
	members: { singular: "member", plural: "members" },
	users: { singular: "user", plural: "users" },
};

function capitalize(s: string): string {
	return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Resolves the cardholder term for a library, from the Attend context
 * bundle's `settings.cardholder_terminology` (or any object carrying
 * that same field). Accepts `undefined`/`null` so a call site mid-load
 * (context not yet fetched) can pass it straight through.
 */
export function cardholderTerm(settings?: { cardholder_terminology?: string | null } | null): CardholderTerm {
	const raw = settings?.cardholder_terminology ?? "";
	const shape = TERMS[raw] ?? TERMS.customers;
	return {
		singular: shape.singular,
		plural: shape.plural,
		Singular: capitalize(shape.singular),
		Plural: capitalize(shape.plural),
	};
}

/**
 * The term for the library the session is currently looking at. Components
 * that render the word go through CardholderTermController, which keeps this
 * current; module-level label tables and pure string helpers that have no
 * component to hang a controller on read it at render time instead, via
 * `termText()`. Starts at the default so a first paint before any fetch
 * resolves reads the same as an unresolved library would.
 */
let active: CardholderTerm = cardholderTerm(undefined);

export function activeCardholderTerm(): CardholderTerm {
	return active;
}

export function setActiveCardholderTerm(term: CardholderTerm): void {
	active = term;
}

/**
 * Fills the four placeholders {patron}, {patrons}, {Patron} and {Patrons} (or
 * the same four spelled {cardholder}, {cardholders}, {Cardholder} and
 * {Cardholders}, for text that must not contain the word "patron", such as the
 * help articles) in a static label with the library's word. This is what lets a module-level
 * table (nav labels, display-label maps) carry copy that names the people a
 * library serves without hard-coding which word they are.
 */
export function termText(text: string, term: CardholderTerm = active): string {
	return text
		.replace(/\{(?:patrons|cardholders)\}/g, term.plural)
		.replace(/\{(?:Patrons|Cardholders)\}/g, term.Plural)
		.replace(/\{(?:patron|cardholder)\}/g, term.singular)
		.replace(/\{(?:Patron|Cardholder)\}/g, term.Singular);
}

/**
 * `termText` over every string in a label table (an options array, a
 * record of chip labels), leaving its shape alone. Wrap a module-level table
 * where it is rendered, so the words are filled per render, not once at import.
 */
export function termDeep<T>(value: T, term: CardholderTerm = active): T {
	if (typeof value === "string") return termText(value, term) as T;
	if (Array.isArray(value)) return value.map((v) => termDeep(v, term)) as T;
	if (value && typeof value === "object") {
		return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, termDeep(v, term)])) as T;
	}
	return value;
}
